import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import {
  validateEmail,
  validatePhoneNumber,
  validateString,
  validationLength,
  validationType,
} from "../../actions/validations";
import {
  reserveInstructorsFieldErrors,
  reserveInstructorsFieldHelperText,
} from "../../../constants/reserveValidationHelperText";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import { ReservePropertyEnum } from "../../../constants/NavbarList";
import {
  RESERVE_COUNTRY_CODE_MAX_DIGITS,
  RESERVE_INSTRUCTORS_EXPERTISE_NONE,
  RESERVE_INSTRUCTORS_EXPERTISE_OPTIONS,
  RESERVE_INSTRUCTORS_EXPERTISE_TRAINER,
} from "../../../constants/ReserveListScreensFields";
import {
  reserveInstructorsTooltip,
  reserveListAvailableHeadingTooltip,
} from "../../../constants/ReserveTooltipInformation";
import useScrollToTop from "../../hooks/useScrollToTop";

const ReserveInstructors = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [employeeCode, setEmployeeCode] = useState("");
  const [expertiseLevel, setExpertiseLevel] = useState<string>(
    RESERVE_INSTRUCTORS_EXPERTISE_NONE,
  );
  const [countryCode, setCountryCode] = useState("");
  const [mobile, setMobile] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [firstNameErr, setFirstNameErr] = useState<boolean>(false);
  const [lastNameErr, setLastNameErr] = useState<boolean>(false);
  const [employeeCodeErr, setEmployeeCodeErr] = useState<boolean>(false);
  const [expertiseLevelErr, setExpertiseLevelErr] = useState<boolean>(false);
  const [countryCodeErr, setCountryCodeErr] = useState<boolean>(false);
  const [mobileErr, setMobileErr] = useState<boolean>(false);
  const [emailErr, setEmailErr] = useState<boolean>(false);
  const [instructorInformation, setInstructorInformation] = useState<any>([]);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const fnTrim = firstName.trim();
  const lnTrim = lastName.trim();
  const empTrim = employeeCode.trim();
  const expertiseNorm =
    expertiseLevel === RESERVE_INSTRUCTORS_EXPERTISE_TRAINER
      ? RESERVE_INSTRUCTORS_EXPERTISE_TRAINER
      : RESERVE_INSTRUCTORS_EXPERTISE_NONE;
  const fullNameTrim = `${fnTrim} ${lnTrim}`.trim();
  const displayLine =
    expertiseNorm === RESERVE_INSTRUCTORS_EXPERTISE_TRAINER
      ? fullNameTrim
        ? `${fullNameTrim} - ${RESERVE_INSTRUCTORS_EXPERTISE_TRAINER}`
        : RESERVE_INSTRUCTORS_EXPERTISE_TRAINER
      : fullNameTrim;

  const payload = {
    "First Name": fnTrim,
    "Last Name": lnTrim,
    "Employee code": empTrim,
    "Expertise level": expertiseNorm,
    "Country Code": countryCode.trim(),
    Mobile: mobile.trim(),
    "Email address": emailAddress.trim(),
    "Name - Instructor Level": displayLine,
    Code: empTrim || displayLine,
    Name: displayLine,
  };

  const validateInput = (): boolean => {
    setFirstNameErr(false);
    setLastNameErr(false);
    setEmployeeCodeErr(false);
    setExpertiseLevelErr(false);
    setCountryCodeErr(false);
    setMobileErr(false);
    setEmailErr(false);

    let valid = true;

    if (!fnTrim) {
      setFirstNameErr(true);
      valid = false;
    } else if (
      !validateString(fnTrim, validationLength.firstName, validationType.SPACE)
    ) {
      setFirstNameErr(true);
      valid = false;
    }

    if (!lnTrim) {
      setLastNameErr(true);
      valid = false;
    } else if (
      !validateString(lnTrim, validationLength.lastName, validationType.SPACE)
    ) {
      setLastNameErr(true);
      valid = false;
    }

    if (!empTrim) {
      setEmployeeCodeErr(true);
      valid = false;
    } else if (
      !validateString(
        empTrim,
        validationLength.roomFeatureName,
        validationType.NAME,
      )
    ) {
      setEmployeeCodeErr(true);
      valid = false;
    }

    if (
      !expertiseLevel.trim() ||
      !RESERVE_INSTRUCTORS_EXPERTISE_OPTIONS.includes(
        expertiseLevel as (typeof RESERVE_INSTRUCTORS_EXPERTISE_OPTIONS)[number],
      )
    ) {
      setExpertiseLevelErr(true);
      valid = false;
    }

    const cc = countryCode.trim();
    if (
      cc &&
      (!/^\d+$/.test(cc) || cc.length > RESERVE_COUNTRY_CODE_MAX_DIGITS)
    ) {
      setCountryCodeErr(true);
      valid = false;
    }

    if (mobile.trim() && !validatePhoneNumber(mobile.trim())) {
      setMobileErr(true);
      valid = false;
    }

    if (emailAddress.trim() && !validateEmail(emailAddress.trim())) {
      setEmailErr(true);
      valid = false;
    }

    return valid;
  };

  useEffect(() => {
    if (fieldValues) {
      setInstructorInformation(
        Array.isArray(fieldValues)
          ? updateData(fieldValues).map((item: any) => ({
              ...item,
              "Sr. No": String(item.id),
            }))
          : [],
      );
    }
  }, []);

  useEffect(() => {
    data(instructorInformation);
    editClicked &&
      setSelectedId(
        checkData(instructorInformation, selectedId) ? selectedId : null,
      );
  }, [instructorInformation]);

  const addInstructor = () => {
    if (!validateInput()) return;
    if (checkDuplicate(instructorInformation ?? [], "Employee code", empTrim)) {
      setAlertMessage("Cannot have duplicate employee codes");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setInstructorInformation((prev: any) =>
      updateData(
        Array.isArray(prev) && prev.length
          ? [...prev, { id: prev.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ).map((item: any) => ({
        ...item,
        "Sr. No": String(item.id),
      })),
    );
    setLastUpdatedRecord(payload);
    editInstructor(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${ReservePropertyEnum.instructors} added successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const updateInstructor = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(
        (instructorInformation ?? []).filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        "Employee code",
        empTrim,
      )
    ) {
      setAlertMessage("Cannot have duplicate employee codes");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setInstructorInformation((prev: any) =>
      updateData(
        (Array.isArray(prev) ? prev : []).map((item: any) =>
          String(item.id) === String(selectedId)
            ? { ...item, ...payload }
            : item,
        ),
      ).map((item: any) => ({
        ...item,
        "Sr. No": String(item.id),
      })),
    );
    editInstructor(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${ReservePropertyEnum.instructors} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const editInstructor = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    if (list == null) {
      setFirstName("");
      setLastName("");
      setEmployeeCode("");
      setExpertiseLevel(RESERVE_INSTRUCTORS_EXPERTISE_NONE);
      setCountryCode("");
      setMobile("");
      setEmailAddress("");
      setFirstNameErr(false);
      setLastNameErr(false);
      setEmployeeCodeErr(false);
      setExpertiseLevelErr(false);
      setCountryCodeErr(false);
      setMobileErr(false);
      setEmailErr(false);
      setEditClicked(false);
      return;
    }
    const hasFirst =
      list["First Name"] != null && String(list["First Name"]).trim() !== "";
    const hasLast =
      list["Last Name"] != null && String(list["Last Name"]).trim() !== "";
    if (hasFirst || hasLast) {
      setFirstName(hasFirst ? String(list["First Name"]) : "");
      setLastName(hasLast ? String(list["Last Name"]) : "");
    } else if (
      list["Name - Instructor Level"] != null &&
      String(list["Name - Instructor Level"]).length > 0
    ) {
      const legacy = String(list["Name - Instructor Level"]);
      const dash = legacy.lastIndexOf(" - ");
      if (
        dash > 0 &&
        legacy.slice(dash + 3) === RESERVE_INSTRUCTORS_EXPERTISE_TRAINER
      ) {
        const namePart = legacy.slice(0, dash).trim();
        const parts = namePart.split(/\s+/);
        setFirstName(parts[0] ?? "");
        setLastName(parts.slice(1).join(" "));
        setExpertiseLevel(RESERVE_INSTRUCTORS_EXPERTISE_TRAINER);
      } else {
        setFirstName("");
        setLastName(legacy);
        setExpertiseLevel(RESERVE_INSTRUCTORS_EXPERTISE_NONE);
      }
    } else {
      setFirstName("");
      setLastName("");
    }
    setEmployeeCode(
      list["Employee code"] != null ? String(list["Employee code"]) : "",
    );
    const ex = list["Expertise level"];
    if (
      ex === RESERVE_INSTRUCTORS_EXPERTISE_TRAINER ||
      String(ex ?? "")
        .trim()
        .toLowerCase() === "trainer"
    ) {
      setExpertiseLevel(RESERVE_INSTRUCTORS_EXPERTISE_TRAINER);
    } else {
      setExpertiseLevel(RESERVE_INSTRUCTORS_EXPERTISE_NONE);
    }
    setCountryCode(
      list["Country Code"] != null ? String(list["Country Code"]) : "",
    );
    setMobile(list.Mobile != null ? String(list.Mobile) : "");
    setEmailAddress(
      list["Email address"] != null ? String(list["Email address"]) : "",
    );
    setFirstNameErr(false);
    setLastNameErr(false);
    setEmployeeCodeErr(false);
    setExpertiseLevelErr(false);
    setCountryCodeErr(false);
    setMobileErr(false);
    setEmailErr(false);
  };

  const deleteInstructor = (id: number) => {
    setInstructorInformation(
      updateData(deleteItem(instructorInformation ?? [], id)).map(
        (item: any) => ({
          ...item,
          "Sr. No": String(item.id),
        }),
      ),
    );
  };

  const firstNameHelperText = firstNameErr
    ? !fnTrim
      ? reserveInstructorsFieldErrors.required
      : reserveInstructorsFieldErrors.firstName
    : reserveInstructorsFieldHelperText.firstName;

  const lastNameHelperText = lastNameErr
    ? !lnTrim
      ? reserveInstructorsFieldErrors.required
      : reserveInstructorsFieldErrors.lastName
    : reserveInstructorsFieldHelperText.lastName;

  const employeeCodeHelperText = employeeCodeErr
    ? !empTrim
      ? reserveInstructorsFieldErrors.required
      : reserveInstructorsFieldErrors.employeeCode
    : reserveInstructorsFieldHelperText.employeeCode;

  const expertiseLevelHelperText = expertiseLevelErr
    ? reserveInstructorsFieldErrors.expertiseLevel
    : reserveInstructorsFieldHelperText.expertiseLevel;

  const countryCodeHelperText = countryCodeErr
    ? reserveInstructorsFieldErrors.countryCode
    : reserveInstructorsFieldHelperText.countryCode;

  const mobileHelperText = mobileErr
    ? reserveInstructorsFieldErrors.mobile
    : reserveInstructorsFieldHelperText.mobile;

  const emailHelperText = emailErr
    ? reserveInstructorsFieldErrors.emailInvalid
    : reserveInstructorsFieldHelperText.emailAddress;

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            gap="10px"
            alignItems="center"
            justifyContent="space-between"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              {ReservePropertyEnum.instructors}
              <Tooltip
                title={tooltipComponent(reserveInstructorsTooltip.header)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editInstructor(null);
                    setSelectedId(null);
                    setEditClicked(false);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editInstructor({ ...lastUpdatedRecord, id: undefined });
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
            <Stack flex={1}>
              <FormLabel component="legend" required>
                First Name
                <Tooltip
                  title={tooltipComponent(reserveInstructorsTooltip.firstName)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="reserve-instructor-first-name"
                variant="outlined"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  setFirstNameErr(false);
                }}
                error={firstNameErr}
                helperText={firstNameHelperText}
                required
                fullWidth
              />
            </Stack>
            <Stack flex={1}>
              <FormLabel component="legend" required>
                Last Name
                <Tooltip
                  title={tooltipComponent(reserveInstructorsTooltip.lastName)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="reserve-instructor-last-name"
                variant="outlined"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  setLastNameErr(false);
                }}
                error={lastNameErr}
                helperText={lastNameHelperText}
                required
                fullWidth
              />
            </Stack>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
            <Stack flex={1}>
              <FormLabel component="legend" required>
                Employee code
                <Tooltip
                  title={tooltipComponent(
                    reserveInstructorsTooltip.employeeCode,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="reserve-instructor-employee-code"
                variant="outlined"
                placeholder="Employee code"
                value={employeeCode}
                onChange={(e) => {
                  setEmployeeCode(e.target.value);
                  setEmployeeCodeErr(false);
                }}
                error={employeeCodeErr}
                helperText={employeeCodeHelperText}
                required
                fullWidth
              />
            </Stack>
            <Stack flex={1}>
              <FormLabel component="legend" required>
                Expertise level
                <Tooltip
                  title={tooltipComponent(
                    reserveInstructorsTooltip.expertiseLevel,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <FormControl
                fullWidth
                size="small"
                required
                error={expertiseLevelErr}
              >
                <InputLabel id="reserve-instructor-expertise-label" required>
                  Expertise level
                </InputLabel>
                <Select
                  labelId="reserve-instructor-expertise-label"
                  id="reserve-instructor-expertise"
                  label="Expertise level"
                  value={expertiseLevel}
                  onChange={(e) => {
                    setExpertiseLevel(e.target.value as string);
                    setExpertiseLevelErr(false);
                  }}
                >
                  <MenuItem value={RESERVE_INSTRUCTORS_EXPERTISE_NONE}>
                    {RESERVE_INSTRUCTORS_EXPERTISE_NONE}
                  </MenuItem>
                  <MenuItem value={RESERVE_INSTRUCTORS_EXPERTISE_TRAINER}>
                    {RESERVE_INSTRUCTORS_EXPERTISE_TRAINER}
                  </MenuItem>
                </Select>
                <FormHelperText error={expertiseLevelErr}>
                  {expertiseLevelHelperText}
                </FormHelperText>
              </FormControl>
            </Stack>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
            <Stack flex={1}>
              <FormLabel component="legend">
                Country Code
                <Tooltip
                  title={tooltipComponent(
                    reserveInstructorsTooltip.countryCode,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="reserve-instructor-country-code"
                variant="outlined"
                placeholder="e.g. +1"
                value={countryCode}
                onChange={(e) => {
                  setCountryCode(e.target.value);
                  setCountryCodeErr(false);
                }}
                error={countryCodeErr}
                helperText={countryCodeHelperText}
                fullWidth
              />
            </Stack>
            <Stack flex={1}>
              <FormLabel component="legend">
                Mobile
                <Tooltip
                  title={tooltipComponent(reserveInstructorsTooltip.mobile)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="reserve-instructor-mobile"
                variant="outlined"
                placeholder="Mobile"
                value={mobile}
                onChange={(e) => {
                  setMobile(e.target.value);
                  setMobileErr(false);
                }}
                error={mobileErr}
                helperText={mobileHelperText}
                fullWidth
              />
            </Stack>
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Email address
              <Tooltip
                title={tooltipComponent(reserveInstructorsTooltip.emailAddress)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="reserve-instructor-email"
              variant="outlined"
              type="email"
              placeholder="Email address"
              value={emailAddress}
              onChange={(e) => {
                setEmailAddress(e.target.value);
                setEmailErr(false);
              }}
              error={emailErr}
              helperText={emailHelperText}
              fullWidth
            />
          </Stack>

          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={() =>
                selectedId ? updateInstructor() : addInstructor()
              }
              id="addReserveInstructor"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD}{" "}
              {ReservePropertyEnum.instructors}
            </Button>
          </Stack>

          <Stack direction="column" gap="10px" mt={4}>
            <Typography
              variant="h6"
              component="div"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              {ReservePropertyEnum.instructors} available
              <Tooltip
                title={tooltipComponent(reserveListAvailableHeadingTooltip)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {Array.isArray(instructorInformation) &&
              instructorInformation.length > 0 ? (
                <DraggableItemList
                  itemData={instructorInformation}
                  onOrderUpdate={(order) =>
                    setInstructorInformation(
                      updateData(order).map((item: any) => ({
                        ...item,
                        "Sr. No": String(item.id),
                      })),
                    )
                  }
                  onEdit={(info) => {
                    editInstructor(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteInstructor(id)}
                  code="Employee code"
                  name="Name - Instructor Level"
                />
              ) : (
                <Typography variant="body2">
                  No {ReservePropertyEnum.instructors} information available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default ReserveInstructors;
